import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Check, X, KeyRound, ShieldCheck } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { Dialog } from '../components/ui/Dialog';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/Table';
import { Textarea } from '../components/ui/Input';
import { PageLoader, Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch, ApiError } from '../lib/apiFetch';
import { formatUzs, formatDateTime } from '../lib/format';
import { useT } from '../i18n';
import type { TKey } from '../i18n/ru';

interface PaymentItem {
  id: number;
  user_id: number;
  user_full_name: string | null;
  user_username: string | null;
  course_id: number;
  course_title: string;
  amount_uzs: string;
  status_code: 'pending' | 'approved' | 'rejected' | 'cancelled';
  receipt_file_id: string | null;
  receipt_caption: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  reject_reason: string | null;
  access_granted_at: string | null;
  access_invite_url: string | null;
}

function ReceiptView({ paymentId, caption }: { paymentId: number; caption: string | null }) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Используем ref, чтобы cleanup мог отозвать URL даже если он был создан
  // ПОСЛЕ того, как React уже захватил замыкание (race fix).
  const objUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setUrl(null);
    setError(null);
    objUrlRef.current = null;

    (async () => {
      try {
        const resp = await fetch(`/api/admin/payments/${paymentId}/receipt`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!resp.ok) {
          if (!cancelled) setError(`HTTP ${resp.status}`);
          return;
        }
        const blob = await resp.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        objUrlRef.current = objUrl;
        // Если пока загружали — компонент размонтировали, revoke сейчас
        if (cancelled) {
          URL.revokeObjectURL(objUrl);
          objUrlRef.current = null;
          return;
        }
        setUrl(objUrl);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'fetch failed');
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (objUrlRef.current) {
        URL.revokeObjectURL(objUrlRef.current);
        objUrlRef.current = null;
      }
    };
  }, [paymentId]);

  return (
    <div className="space-y-2">
      {!url && !error && (
        <div className="flex h-64 items-center justify-center">
          <Spinner className="h-8 w-8" />
        </div>
      )}
      {error && <p className="text-sm text-red-600">{t('payments.receiptLoadFailed', { error })}</p>}
      {url && <img src={url} alt="Receipt" className="mx-auto max-h-[60vh] rounded-md" />}
      {caption && <p className="text-sm text-slate-600">{t('payments.receiptComment', { caption })}</p>}
    </div>
  );
}

const STATUS_KEY: Record<PaymentItem['status_code'], TKey> = {
  pending: 'payments.statusPending',
  approved: 'payments.statusApproved',
  rejected: 'payments.statusRejected',
  cancelled: 'payments.statusCancelled',
};
const STATUS_VARIANT: Record<PaymentItem['status_code'], BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'destructive',
  cancelled: 'muted',
};

export function Payments() {
  const t = useT();
  const [status, setStatus] = useState<string>('pending');
  const [viewing, setViewing] = useState<PaymentItem | null>(null);
  const [rejecting, setRejecting] = useState<PaymentItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const qc = useQueryClient();
  const toast = useToast();

  const list = useQuery({
    queryKey: ['payments', status],
    queryFn: () =>
      apiFetch<{ items: PaymentItem[]; total: number }>('/api/admin/payments', {
        query: { status: status === 'all' ? '' : status, limit: 50, offset: 0 },
      }),
  });

  const approve = useMutation({
    mutationFn: (paymentId: number) =>
      apiFetch(`/api/admin/payments/${paymentId}/approve`, { method: 'POST' }),
    onSuccess: () => {
      toast.success(t('payments.approved'));
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err) => toast.error((err as ApiError).message ?? t('payments.approveFailed')),
  });

  const reject = useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: number; reason: string }) =>
      apiFetch(`/api/admin/payments/${paymentId}/reject`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      setRejecting(null);
      setRejectReason('');
      toast.success(t('payments.rejected'));
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err) => toast.error((err as ApiError).message ?? t('payments.rejectFailed')),
  });

  const retryIssueAccess = useMutation({
    mutationFn: ({ paymentId, force }: { paymentId: number; force?: boolean }) =>
      apiFetch(`/api/admin/payments/${paymentId}/issue-access`, {
        method: 'POST',
        query: force ? { force: '1' } : {},
      }),
    onSuccess: () => {
      toast.success(t('payments.inviteSent'));
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err) => {
      const e = err as ApiError;
      if (e.code === 'already_granted') {
        // Дату берём из машиночитаемого поля — message с сервера всегда русский
        const grantedAt = e.details?.granted_at;
        const already =
          typeof grantedAt === 'string'
            ? t('payments.alreadyGranted', { when: formatDateTime(grantedAt) })
            : e.message;
        if (confirm(`${already}\n\n${t('payments.reissueConfirm')}`)) {
          // Получаем paymentId из переменных предыдущего вызова
          const v = retryIssueAccess.variables;
          if (v) retryIssueAccess.mutate({ paymentId: v.paymentId, force: true });
        }
        return;
      }
      toast.error(t('payments.issueFailed', { message: e.message }));
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatus(s)}
          >
            {s === 'all' ? t('common.all') : t(STATUS_KEY[s as PaymentItem['status_code']])}
          </Button>
        ))}
      </div>

      {list.isLoading ? (
        <PageLoader />
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState title={t('payments.emptyTitle')} description={t('payments.emptyDescription')} />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>#</TH>
                  <TH>{t('payments.student')}</TH>
                  <TH>{t('payments.course')}</TH>
                  <TH>{t('payments.amount')}</TH>
                  <TH>{t('payments.receivedAt')}</TH>
                  <TH>{t('common.status')}</TH>
                  <TH className="text-right">{t('common.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.items.map((p) => (
                  <TR key={p.id}>
                    <TD className="font-mono text-slate-500">#{p.id}</TD>
                    <TD>
                      <div className="font-medium">{p.user_full_name ?? '—'}</div>
                      {p.user_username && <div className="text-xs text-slate-500">@{p.user_username}</div>}
                    </TD>
                    <TD>{p.course_title}</TD>
                    <TD>{formatUzs(p.amount_uzs)}</TD>
                    <TD className="whitespace-nowrap">{formatDateTime(p.submitted_at)}</TD>
                    <TD>
                      <Badge variant={STATUS_VARIANT[p.status_code]}>{t(STATUS_KEY[p.status_code])}</Badge>
                    </TD>
                    <TD className="text-right">
                      <div className="inline-flex gap-1">
                        {p.receipt_file_id && (
                          <Button size="sm" variant="outline" onClick={() => setViewing(p)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {p.status_code === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="success"
                              loading={approve.isPending && approve.variables === p.id}
                              onClick={() => approve.mutate(p.id)}
                            >
                              <Check className="h-4 w-4" />
                              {t('payments.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setRejecting(p)}
                            >
                              <X className="h-4 w-4" />
                              {t('payments.reject')}
                            </Button>
                          </>
                        )}
                        {p.status_code === 'approved' && !p.access_granted_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            loading={
                              retryIssueAccess.isPending &&
                              retryIssueAccess.variables?.paymentId === p.id
                            }
                            onClick={() => retryIssueAccess.mutate({ paymentId: p.id })}
                            title={t('payments.issueAccessHint')}
                          >
                            <KeyRound className="h-4 w-4" />
                            {t('payments.issueAccess')}
                          </Button>
                        )}
                        {p.status_code === 'approved' && p.access_granted_at && (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-emerald-700"
                            title={t('payments.accessGrantedAt', {
                              when: formatDateTime(p.access_granted_at),
                            })}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            {t('payments.accessGranted')}
                          </span>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={t('payments.receiptTitle', { id: viewing?.id ?? '' })}
        size="lg"
      >
        {viewing && (
          <ReceiptView paymentId={viewing.id} caption={viewing.receipt_caption} />
        )}
      </Dialog>

      <Dialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={t('payments.rejectTitle', { id: rejecting?.id ?? '' })}
        description={t('payments.rejectDescription')}
      >
        <div className="space-y-3">
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('payments.rejectPlaceholder')}
            rows={4}
          />
          {reject.isError && (
            <p className="text-sm text-red-600">{(reject.error as ApiError).message}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejecting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim()}
              loading={reject.isPending}
              onClick={() => rejecting && reject.mutate({ paymentId: rejecting.id, reason: rejectReason.trim() })}
            >
              {t('payments.reject')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
